import { AfterContentInit, AfterViewInit, Component, ComponentFactoryResolver, Input, OnInit, QueryList, Type, ViewChildren, ViewContainerRef } from '@angular/core';
import { DynamicAnchorDirective } from './directives/dynamic-anchor.directive';
import { Charts, IChartModule } from './charts.class'
import { BasicLinebarChartComponent } from './basic-linebar-chart/basic-linebar-chart.component';
import { ChartLayout, ChartTypes, ILayout } from './charts.class';
import { IMapTemplate } from '../csv-mapper/csv-map.class';

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.sass']
})
export class ChartsComponent implements OnInit, AfterViewInit, AfterContentInit {

  objKeys = Object.keys

  Layout = ChartLayout

  @Input('layout') set layout(arg: ILayout) {
    if (!arg) return

    const { orient, isDev } = arg
    const layout = this.ui.layout

    layout.orient = orient || ChartLayout.horizontal
    layout.isDev = isDev || false
  }

  @Input('charts') charts: {
    [chartKey: string]: IChartObj
  } = {
      test1: {
        name: 'First',
        chart: ChartTypes.basicLB,
        mapping: tempMapping,
        data: tempData
      },
      test2: {
        name: 'Second',
        chart: ChartTypes.basicLB,
        mapping: tempMapping,
        data: tempData
      }
    }

  @ViewChildren(DynamicAnchorDirective) anchors: QueryList<DynamicAnchorDirective>

  /**
   * UI variable that stores vars specifically to the template
   */
  ui: {
    layout: ILayout
    style: {
      width: string,
      height: string
    }
  } = {
      layout: {
        orient: ChartLayout.horizontal,
        isDev: false
      },
      style: {
        width: '',
        height: ''
      }
    }

  /** List of chart components for initialization */
  CHARTCOMPS: {
    [id: string]: ChartList
  } = {}

  get isHorizontal() {
    return this.ui?.layout?.orient === ChartLayout.horizontal
  }

  constructor(
    private compFactResolver: ComponentFactoryResolver
  ) { }

  ngOnInit(): void {
    this.initChartsObj()
  }

  ngAfterViewInit(): void {
    this.loadCharts()
  }

  ngAfterContentInit(): void {
    this.calculateDimension()
  }

  calculateDimension() {
    const charts = Object.keys(this.charts)

    /** Precalc styling */
    const percent = 100 / (charts?.length || 1)
    if (this.isHorizontal) {
      this.ui.style.width = `${percent}%`
      this.ui.style.height = ``
    }
    else {
      this.ui.style.height = `${percent}%`
      this.ui.style.width = ``
    }
  }

  /** Load chart components and their data */
  loadCharts() {
    const anchors = this.anchors
      , charts = Object.keys(this.charts)

    /** 
     * Check to ensure length is the same as object 
     */
    if (charts.length !== anchors.length)
      throw new Error('Unable to load charts. Charts number and anchors do not match')

    anchors.map((anchor, idx) => {
      const chart = this.charts[charts[idx]]
      const chartRef = this.CHARTCOMPS

      const selChart = chartRef[chart.chart]

      if (!selChart) {
        console.warn('loadCharts().Chart type does not match available charts')
        return
      }
      else {
        if (idx === 0) {
          setTimeout(() => {
            /** If there's any problem, we'll need to async await properly */
            this.injectComponent(anchor.viewContainerRef, selChart.component, chart)
          }, 10000)
        }
        else {
          /** If there's any problem, we'll need to async await properly */
          this.injectComponent(anchor.viewContainerRef, selChart.component, chart)
        }
      }

    })
  }

  /**
   * Injects a specific component in the exact viewContainerRef
   * @param vCRef viewContainerRef, ie the anchor for the component to injected into
   * @param component Component with type IChartModule
   */
  async injectComponent(vCRef: ViewContainerRef, component: Type<IChartModule>, chartData?: IChartObj): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const compFactory = this.compFactResolver.resolveComponentFactory<IChartModule>(component)
        const anchorRef = vCRef
        anchorRef.clear()
    
        const compRef = anchorRef.createComponent<IChartModule>(compFactory)
        /** 
         * Very inelegant way to load the component variables 
         * Meant to remove error with contentChange
         */
        setTimeout(() => {
          compRef.instance.id = `${makeid(5)}`
          compRef.instance.mapping = chartData.mapping
          compRef.instance.data = chartData.data
          resolve(true)
        }, 100)
      }
      catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Generate chart lists for dynamic generation
   */
  initChartsObj() {
    const basicLB = new ChartList(BasicLinebarChartComponent)
    this.CHARTCOMPS[basicLB?.schema?.id] = basicLB
  }
}

interface IChartObj {
  name: string,
  chart: ChartTypes
  mapping: IMapTemplate,
  data: any[]
}

/**
 * Chart List as gallery 
 */
class ChartList {
  component: Type<IChartModule>
  schema: IMapTemplate

  constructor(component: any) {
    if ('SCHEMA' in component) {
      const SCHEMA = component.SCHEMA as IMapTemplate

      this.schema = SCHEMA

      this.component = component
    }
  }
}

/**
 * Potential function for used elsewhere
 * Taken from https://stackoverflow.com/a/1349426
 */
function makeid(length: number) {
  let result           = '';
  const characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for ( let i = 0; i < length; i++ ) {
     result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

const tempData = [
  { day: 1, breakfast: 0, lunch: 5, dinner: 0 },
  { day: 2, breakfast: 1, lunch: 3, dinner: 0 },
  { day: 3, breakfast: 1, lunch: 10, dinner: 0 },
  { day: 4, breakfast: 2, lunch: 8, dinner: 0 },
  { day: 5, breakfast: 0, lunch: 1, dinner: 0 },
  { day: 6, breakfast: 3, lunch: 11, dinner: 0 },
  { day: 7, breakfast: 0, lunch: 11, dinner: 0 },
  { day: 8, breakfast: 4, lunch: 6, dinner: 0 },
  { day: 9, breakfast: 0, lunch: 8, dinner: 0 },
  { day: 10, breakfast: 2, lunch: 11, dinner: 0 },
  { day: 11, breakfast: 0, lunch: 11, dinner: 0 },
  { day: 12, breakfast: 0, lunch: 6, dinner: 0 },
  { day: 13, breakfast: 3, lunch: 8, dinner: 0 },
  { day: 14, breakfast: 0, lunch: 5, dinner: 0 },
  { day: 15, breakfast: 3, lunch: 4, dinner: 0 },
  { day: 16, breakfast: 3, lunch: 0, dinner: 0 },
  { day: 17, breakfast: 0, lunch: 0, dinner: 0 },
  { day: 18, breakfast: 4, lunch: 0, dinner: 0 },
  { day: 19, breakfast: 2, lunch: 11, dinner: 0 },
  { day: 20, breakfast: 4, lunch: 8, dinner: 0 },
  { day: 21, breakfast: 2, lunch: 4, dinner: 0 },
  { day: 22, breakfast: 4, lunch: 4, dinner: 0 },
  { day: 23, breakfast: 0, lunch: 2, dinner: 0 },
  { day: 24, breakfast: 3, lunch: 5, dinner: 0 },
  { day: 25, breakfast: 2, lunch: 11, dinner: 0 },
  { day: 26, breakfast: 2, lunch: 6, dinner: 0 },
  { day: 27, breakfast: 1, lunch: 9, dinner: 0 },
  { day: 28, breakfast: 4, lunch: 11, dinner: 0 },
  { day: 29, breakfast: 2, lunch: 7, dinner: 0 },
  { day: 30, breakfast: 0, lunch: 4, dinner: 0 }
]

const tempMapping: IMapTemplate = {
  name: 'Basic Line Bar Chart',
  id: ChartTypes.basicLB,
  fields: [
    {
      text: 'Date',
      placeholder: 'X-Axis',
      id: 'xaxis',
      key: 'day'
    },
    {
      text: 'Breakfast cost',
      placeholder: 'Y-Axis',
      id: 'yaxis',
      key: 'breakfast'
    }
  ]
}
